import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Input from '../components/Input';
import Button from '../components/Button';
import Card from '../components/Card';
import OrcidButton from '../components/OrcidButton';
import GoogleButton from '../components/GoogleButton';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();

  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const roleParam = searchParams.get('role');
  const validRoles = ['author', 'reviewer', 'participant', 'organizer', 'admin'];
  const isValidRole = roleParam && validRoles.includes(roleParam);

  useEffect(() => {
    if (user) {
      navigate(`/${user.role}/dashboard`);
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.identifier.trim()) {
      errors.identifier = 'Email or username is required';
    } else if (formData.identifier.trim().length > 254) {
      errors.identifier = 'Identifier is too long';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length > 128) {
      errors.password = 'Password is too long';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    const res = await login(formData.identifier, formData.password);

    if (!res || !res.success) {
      setError(res?.message || 'Login failed');
      setLoading(false);
      return;
    }

    // defensive role extraction
    const role = res.user?.role || res.raw?.user?.role || res.raw?.role || null;
    if (role === 'admin') navigate('/admin/dashboard');
    else if (role === 'organizer') navigate('/organizer/dashboard');
    else if (role === 'author') navigate('/author/dashboard');
    else if (role === 'reviewer') navigate('/reviewer/dashboard');
    else if (role === 'participant') navigate('/participant/dashboard');
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="/resized-logo.jpg"
              alt="eConfMate Logo"
              className="h-16 w-16 object-contain transition-transform duration-200 hover:scale-110"
            />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-600 via-primary-700 to-blue-600 bg-clip-text text-transparent">
              eConfMate
            </h1>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h2>
          <p className="text-gray-600">
            {isValidRole ? `Login as ${roleParam}` : 'Sign in to your account'}
          </p>
        </div>

        {/* Login Card */}
        <Card>
          <form onSubmit={handleSubmit}>
            <Input
              label="Email or Username"
              type="text"
              name="identifier"
              value={formData.identifier}
              onChange={handleChange}
              placeholder="you@example.com or username"
              required
              error={fieldErrors.identifier}
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              error={fieldErrors.password}
            />

            <div className="flex justify-end mb-4 -mt-2">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Forgot Password?
              </Link>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            {roleParam === 'organizer' && (
              <div className="mt-6 text-center">
                <div className="text-sm text-gray-600 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <svg className="w-5 h-5 text-blue-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-medium text-gray-800 mb-1">First time logging in?</p>
                  <p>Your account was provisioned by an administrator. Use <strong>Forgot Password?</strong> above to set your own secure password with the OTP sent to your email.</p>
                </div>
              </div>
            )}
          </form>

          {roleParam !== 'organizer' && roleParam !== 'admin' && (
            <>
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with</span>
                </div>
              </div>

              {/* ORCID Login Button */}
              <OrcidButton />

              {/* Google Login Button */}
              <div className="mt-3">
                <GoogleButton />
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  Don't have an account?{' '}
                  <Link
                    to={roleParam ? `/register?role=${roleParam}` : '/register'}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Register here
                  </Link>
                </p>
              </div>
            </>
          )}
        </Card>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
